import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import { FiArrowLeft, FiEye, FiEyeOff, FiKey, FiLock, FiLogIn, FiMail, FiShield } from "react-icons/fi";
import { motion } from "framer-motion";
import ThemeToggle from "../../components/common/ThemeToggle";

type ActiveForm = "login" | "resetEmail" | "resetCode" | "resetPassword";

interface FormDataState {
  email: string;
  password: string;
  codActivation: string;
}

function Field({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="modern-field">
      <span style={{ color: "rgb(93, 173, 226)" }}>{icon}</span>
      {children}
    </label>
  );
}

function PasswordField({
  value,
  onChange,
  placeholder,
  name,
  visible,
  onToggle,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  name?: string;
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <Field icon={<FiLock />}>
      <input
        type={visible ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <button type="button" className="modern-password-toggle" onClick={onToggle}>
        {visible ? <FiEyeOff /> : <FiEye />}
        <span>{visible ? "Ocultar" : "Mostrar"}</span>
      </button>
    </Field>
  );
}

export default function AuthUserToSubmasterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeForm, setActiveForm] = useState<ActiveForm>("login");
  const [formData, setFormData] = useState<FormDataState>({ email: "", password: "", codActivation: "" });
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient();

    const payload = {
      email: formData.email,
      password: formData.password,
      codActivation: formData.codActivation,
    };

    const res = await api.post("/auth/loginUserToSubmaster", payload);

    if (res.data.error) return toast.error("Credenciais inválidas");
    new LocalStorage().saveAuthTokeUserToSubmaster(res.data.token);
    navigate("/HomeUserToSubmasterPage");
  }

  async function handleVerifyEmail(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient();
    const res = await api.post("/auth/verify_email", { email: formData.email });
    if (res.data.login_error) return toast.error("E-mail não encontrado");
    setToken(res.data.token);
    setActiveForm("resetCode");
  }

  async function handleVerifyCode(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient();
    const res = await api.post("/auth/verify_cod", { code, token });
    if (!res.data.success) return toast.error("Código inválido");
    setActiveForm("resetPassword");
  }

  async function handleResetPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient(token);
    await api.post("/auth/resetpassword", { code, password: formData.password });
    toast.success("Senha atualizada!");
    setActiveForm("login");
  }

  return (
    <div className="modern-auth-page">
      <section className="modern-auth-left d-flex flex-column justify-content-center">
        <div className="modern-auth-brand-copy">
          <div className="modern-auth-badge">
            <FiShield />
            Área de signatários
          </div>
          <h1 className="modern-auth-hero-title">Acesso seguro de signatário</h1>
          <p className="modern-auth-hero-text">
            Consulte e valide documentos com permissões específicas de visualização e edição no fluxo contratual.
          </p>
          {searchParams.get("name") && (
            <p style={{ color: "rgba(255,255,255,0.95)", fontWeight: 700, fontSize: "1.02rem" }}>
              Convite: {searchParams.get("name")}
            </p>
          )}
        </div>
        <div className="auth-docs-3d" aria-hidden="true">
          <div className="auth-doc auth-doc-one">
            <span />
            <span />
            <span className="short" />
          </div>
          <div className="auth-doc auth-doc-two">
            <span />
            <span />
            <span className="short" />
          </div>
          <div className="auth-doc auth-doc-three">
            <span />
            <span />
            <span className="short" />
          </div>
          <div className="auth-doc-seal">OK</div>
        </div>
      </section>

      <section className="modern-auth-right">
        <div className="modern-auth-panel">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button type="button" className="btn btn-link modern-helper-link p-0" onClick={() => navigate("/")}>
              Voltar
            </button>
            <ThemeToggle />
          </div>
          <motion.form
            onSubmit={
              activeForm === "login"
                ? handleLogin
                : activeForm === "resetEmail"
                ? handleVerifyEmail
                : activeForm === "resetCode"
                ? handleVerifyCode
                : handleResetPassword
            }
            className="d-flex flex-column gap-3"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
          >
            {activeForm === "login" && (
              <>
                <h2 className="modern-auth-title h4 fw-bold mb-1">Login de signatário</h2>
                <p className="modern-auth-subtitle">Entre com suas credenciais e código de acesso</p>

                <Field icon={<FiMail />}>
                  <input name="email" value={formData.email} onChange={handleChange} placeholder="E-mail" />
                </Field>

                <PasswordField
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Senha"
                  visible={showPassword}
                  onToggle={() => setShowPassword((prev) => !prev)}
                />

                <Field icon={<FiKey />}>
                  <input
                    type="text"
                    name="codActivation"
                    value={formData.codActivation}
                    onChange={handleChange}
                    placeholder="Código de acesso"
                  />
                </Field>

                <button className="btn modern-primary-btn py-2 fw-semibold mt-2" type="submit">
                  <FiLogIn className="me-2" /> Logar
                </button>

                <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => setActiveForm("resetEmail")}>
                  Esqueci minha senha
                </button>

                <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => navigate("/")}>
                  <FiArrowLeft className="me-1" /> Voltar para área administrativa
                </button>
              </>
            )}

            {activeForm === "resetEmail" && (
              <>
                <h2 className="modern-auth-title h4 fw-bold mb-1">Recuperar senha</h2>
                <p className="modern-auth-subtitle">Informe seu e-mail para validação</p>

                <Field icon={<FiMail />}>
                  <input name="email" value={formData.email} onChange={handleChange} placeholder="E-mail" />
                </Field>

                <button className="btn modern-primary-btn py-2 fw-semibold mt-2" type="submit">Validar e-mail</button>
                <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => setActiveForm("login")}>
                  <FiArrowLeft className="me-1" /> Voltar ao login
                </button>
              </>
            )}

            {activeForm === "resetCode" && (
              <>
                <h2 className="modern-auth-title h4 fw-bold mb-1">Código de segurança</h2>
                <p className="modern-auth-subtitle">Digite o código recebido por e-mail</p>

                <Field icon={<FiKey />}>
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Código" />
                </Field>

                <button className="btn modern-primary-btn py-2 fw-semibold mt-2" type="submit">Validar código</button>
              </>
            )}

            {activeForm === "resetPassword" && (
              <>
                <h2 className="modern-auth-title h4 fw-bold mb-1">Nova senha</h2>
                <p className="modern-auth-subtitle">Defina uma nova senha para seu acesso</p>

                <PasswordField
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Nova senha"
                  visible={showPassword}
                  onToggle={() => setShowPassword((prev) => !prev)}
                />

                <button className="btn modern-primary-btn py-2 fw-semibold mt-2" type="submit">Redefinir senha</button>
              </>
            )}
          </motion.form>
        </div>
      </section>
    </div>
  );
}
